import { AdminMembersReportPageParams } from "@/admin/insights/member-reports/AdminMembersReportPage"
import { MemberReportSurveyContentUsageSelect_PaginationQuery } from "@/admin/insights/member-reports/tabs/contents/__generated__/MemberReportSurveyContentUsageSelect_PaginationQuery.graphql"
import ContentThumbnailWithDetails from "@/content/detail/ContentThumbnailWithDetails"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import { DiscoInputSkeleton, DiscoTooltip } from "@disco-ui"
import DiscoDropdownItem from "@disco-ui/dropdown/DiscoDropdownItem"
import DiscoMultiSelect, {
  DiscoMultiSelectOption,
  DiscoMultiSelectProps,
} from "@disco-ui/select/DiscoMultiSelect"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { TestIDProps } from "@utils/typeUtils"
import { graphql } from "react-relay"

export type MemberReportSurveyContentUsage = {
  id: GlobalID
  productName?: string | null
  module?: { name: string; isCurriculumModule?: boolean | null }
  content: { name: string }
}

interface Props
  extends TestIDProps,
    Pick<DiscoMultiSelectProps, "autoFocusInput" | "limit" | "disableClearable"> {
  values: GlobalID[]
  onChange: (contentUsages: MemberReportSurveyContentUsage[]) => void
  inProductsViewerIsAdminOf?: boolean
}

function MemberReportSurveyContentUsageSelect(props: Props) {
  const { testid = "MemberReportSurveyContentUsageSelect", values, onChange } = props

  const [queryParams] = useQueryParamState<AdminMembersReportPageParams>()

  const { data, pagination } =
    Relay.useRefetchablePaginationQuery<MemberReportSurveyContentUsageSelect_PaginationQuery>(
      graphql`
        query MemberReportSurveyContentUsageSelect_PaginationQuery(
          $id: ID!
          $first: Int!
          $after: String
          $search: String
          $selectedIds: [ID!]
        ) {
          node(id: $id) {
            ... on OrganizationMembership {
              contentUsages(
                after: $after
                first: $first
                search: $search
                contentTypes: [survey]
              ) @connection(key: "MemberReportSurveyContentUsageSelect_contentUsages") {
                edges {
                  node {
                    id
                    ...ContentThumbnailWithDetails_ContentUsageFragment
                    content {
                      name
                      ...ContentThumbnailWithDetails_ContentFragment
                    }
                    product {
                      name
                    }
                    module {
                      name
                      isCurriculumModule
                    }
                  }
                }
                pageInfo {
                  hasNextPage
                  hasPreviousPage
                  startCursor
                  endCursor
                }
              }
              selected: contentUsages(
                contentUsageIds: $selectedIds
                contentTypes: [survey]
              ) {
                edges {
                  node {
                    id
                    ...ContentThumbnailWithDetails_ContentUsageFragment
                    content {
                      name
                      ...ContentThumbnailWithDetails_ContentFragment
                    }
                    product {
                      name
                    }
                    module {
                      name
                      isCurriculumModule
                    }
                  }
                }
              }
            }
          }
        }
      `,
      {
        id: queryParams.om!,
        selectedIds: values,
      },
      { connectionName: "node.contentUsages" }
    )

  if (!data?.node?.contentUsages) return null

  // Combine the selected surveys with the paginated surveys
  // This is needed so the selected surveys are always visible and they may not necessarily be in the paginated list
  const contentUsages = Relay.connectionToArray(data?.node?.contentUsages).concat(
    values.length ? Relay.connectionToArray(data?.node?.selected) : []
  )

  const contentUsagesById = ArrayUtils.mapBy(contentUsages, "id")
  const noSurveys = !contentUsages.length

  return (
    <DiscoTooltip
      titleVariant={"body-sm-600"}
      title={noSurveys ? "No Surveys Available" : null}
      content={undefined}
    >
      <DiscoMultiSelect
        testid={testid}
        placeholder={"Please select a survey"}
        values={values}
        onChange={handleChange}
        renderOption={renderOption}
        renderTags={renderTags}
        options={contentUsages.map((cu) => ({
          key: cu.id,
          value: cu.id,
          title: cu.content.name!,
        }))}
        disabled={noSurveys}
        disableClearable={true}
        pagination={pagination}
        limit={1}
      />
    </DiscoTooltip>
  )

  function handleChange(contentUsageIds: GlobalID[]) {
    onChange(
      contentUsageIds
        .filter((id) => contentUsagesById[id])
        .map(
          (id) =>
            ({ ...contentUsagesById[id] } as unknown as MemberReportSurveyContentUsage)
        )
    )
  }

  function renderTags(value: DiscoMultiSelectOption[]) {
    if (!value.length) return null
    const contentUsage = contentUsagesById[value[0].value]
    return (
      <div>
        <DiscoDropdownItem
          backgroundColor={"transparent"}
          title={contentUsage.content.name!}
        />
      </div>
    )
  }

  function renderOption(option: DiscoMultiSelectOption) {
    const contentUsage = contentUsagesById[option.value]
    if (!contentUsage) return <></>
    return (
      <ContentThumbnailWithDetails
        contentKey={contentUsage.content}
        contentUsageKey={contentUsage}
        variant={"with-location"}
        truncateText={1}
      />
    )
  }
}

export default Relay.withSkeleton<Props>({
  component: MemberReportSurveyContentUsageSelect,
  skeleton: () => <DiscoInputSkeleton />,
})
